import Shared from '../../../Templates/Core/JS/Shared.js';
import { ITokenBasedSessionService, SessionServiceReadyEvent } from '@studyportals/student-interfaces';
import {
	AnonymousStudentProfileSynced,
	AnonymousStudentProfileUpdated,
	AnonymousStudentServiceReady,
	AnonymousStudentStateChanged,
	IAnonymousStudentService,
	StudentRepositoryStateType
} from '@studyportals/anonymous-student-interfaces';
import { IStudent, StudentField } from '@studyportals/studentdomain';
import {
	AuthenticationCompletedEvent,
	AuthenticationServiceReadyEvent,
	IAuthenticationServiceApplication,
	Referrer,
	RenderIncentiveCommand
} from '@studyportals/authentication-service-interfaces';
import { DataStorage } from "@studyportals/data-storage";

export default class HeaderLogin {
	public headerLogin: HTMLElement | null = null;
	public loggedInStatus = false;
	public anonymousStudent: IAnonymousStudentService | null = null;
	public loggedInMenuVisible = false;
	public sessionService: ITokenBasedSessionService | null = null;
	public authService: IAuthenticationServiceApplication | null = null;

	constructor() {
		this.headerLogin = document.getElementById('HeaderLogin');
		this.subscribeToEvents();
	}

	public init(): void {
		if (this.injectHeaderLoginElement()) {
			this.addEventListenerForLoginButton();
		} else {
			console.error('HeaderLogin couldnt inject placeholder');
		}

		this.addEventListenersForWishlistButtons();
		this.showProfileIntroductionIcon();
		this.addEventListenerMobileLoginButton();
	}

	public addEventListenerForLoginButton(): void {
		const loginButton = document.getElementById('LoginButton');
		loginButton?.addEventListener('click', () => {
			const command = new RenderIncentiveCommand(Referrer.HEADER);
			this.authService?.renderIncentive(command);
			this.toggleHeaderFlyOut();

		});
	}

	public addEventListenersForWishlistButtons(): void {
		const wishlistButtons = document.getElementsByClassName('js-wishlistButton');

		Array.from(wishlistButtons).forEach((el) => {
			el.addEventListener('click', () => {
				this.wishlistClickHandler();
			});
		});
	}

	public closeHeaderFlyOutAftedLogin(): void {
		document.addEventListener('authentication_completed', () => {
			this.toggleHeaderFlyOut();
		});
	}

	public toggleHeaderFlyOut(): void {
		this.closeHeaderFlyOutAftedLogin();
		const headerFlyOut = document.getElementById('HeaderFlyoutWrapper');
		if (headerFlyOut === null) return;
		headerFlyOut.classList.toggle('is-open');

		Shared.breakpoints(['Small', 'Medium'], () => {
			const inlineSearch = document.getElementById('InlineHeaderSearchTopContainer');
			if (inlineSearch) {
				if (headerFlyOut.classList.contains('is-open')) {
					inlineSearch.style.zIndex = '-3';
				} else {
					inlineSearch.style.zIndex = '25';
				}
			}
		});
	}

	public injectHeaderLoginElement(): boolean {
		const searchRowWrapper = document.getElementById('SearchRowWrapper');
		if (!searchRowWrapper) {
			return false;
		}

		const headerFlyoutWrapper = document.createElement('div');
		headerFlyoutWrapper.id = 'HeaderFlyoutWrapper';
		const authPlaceholder = document.getElementById('AuthHeaderPlaceholder');

		if (!authPlaceholder) {
			return false;
		}

		headerFlyoutWrapper.appendChild(authPlaceholder);
		searchRowWrapper.appendChild(headerFlyoutWrapper);

		this.adjustMobileFlyoutHeight(headerFlyoutWrapper);

		return true;
	}

	private adjustMobileFlyoutHeight(headerFlyoutWrapper: HTMLDivElement): void {
		Shared.breakpoints(['Small', 'Medium'], () => {
			const searchRow = document.getElementById('SearchRow');

			if (!searchRow) return;

			window.addEventListener('resize', () => {
				const viewportHeight = window.innerHeight;
				const searchRowHeight = searchRow.clientHeight;
				headerFlyoutWrapper.style.maxHeight = `calc(${viewportHeight - searchRowHeight}px - 1rem)`;
			});
		});
	}

	private subscribeToEvents(): void {
		if (!window.EventAggregationService) {
			return;
		}

		window.EventAggregationService.subscribeTo(
			SessionServiceReadyEvent.EventType,
			{
				notify: async (event: SessionServiceReadyEvent) => {
					this.sessionService = event.sessionService;
					await this.updateMenuWhenApplicable();
				}
			},
			true
		);

		window.EventAggregationService.subscribeTo(
			AuthenticationServiceReadyEvent.EventType,
			{
				notify: (event: AuthenticationServiceReadyEvent) => {
					this.authService = event.authenticationServiceApplication;
				}
			},
			true
		);

		window.EventAggregationService.subscribeTo(
			AnonymousStudentServiceReady.EventType,
			{
				notify: async (event: AnonymousStudentServiceReady) => {
					this.anonymousStudent = event.anonymousStudentService;
					await this.updateMenuWhenApplicable();
				}
			},
			true
		);

		window.EventAggregationService.subscribeTo(AnonymousStudentProfileSynced.EventType, {
			notify: async () => {
				await this.updateMenuWhenApplicable();
			}
		});

		window.EventAggregationService.subscribeTo(AnonymousStudentStateChanged.EventType, {
			notify: async () => {
				await this.updateMenuWhenApplicable();
			}
		});

		window.EventAggregationService.subscribeTo(AnonymousStudentProfileUpdated.EventType, {
			notify: (event: AnonymousStudentProfileUpdated) => {
				if (event.state === StudentRepositoryStateType.ONLINE && !!event.changes[StudentField.FIRST_NAME]) {
					this.updateName(event.changes[StudentField.FIRST_NAME]);
				}
			}
		});
	}

	private async updateMenuWhenApplicable(): Promise<void> {
		if (!(this.sessionService && this.anonymousStudent)) {
			return;
		}

		const session = await this.sessionService.getSession();
		if (session) {
			await this.onLogin();
			return;
		}

		this.headerLogin?.classList.remove('Hidden');
	}

	private async onLogin(): Promise<void> {
		this.loggedInStatus = true;

		const student: IStudent | null =
			(await this.anonymousStudent?.getStudentData([StudentField.FIRST_NAME])) || null;
		if (!student) return;

		if (!this.loggedInMenuVisible) {
			this.requestMenu(student);
		}

		this.updateName(student.first_name || '');
	}

	private wishlistClickHandler(): void {
		this.trackEntryPointClick();
		this.conditionallyOpenTheWishlist();
	}

	private conditionallyOpenTheWishlist(): void {
		if (this.loggedInStatus) {
			document.dispatchEvent(new Event('openWishlistDrawer'));
			return;
		}
		const command = new RenderIncentiveCommand(Referrer.WISHLIST);
		this.authService?.renderIncentive(command);

		this.trackIncentiveImpression();
		this.waitForRegistration();
	}

	private trackIncentiveImpression(): void {
		if (typeof window.snowplow !== 'undefined') {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-call
			window.snowplow('trackStructEvent', {
				category: 'Profile',
				action: 'Register Incentive Click',
				label: 'View wishlist header'
			});
		}
	}

	private trackEntryPointClick(): void {
		if (typeof window.snowplow !== 'undefined') {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-call
			window.snowplow('trackStructEvent', {
				category: 'Wishlisting',
				action: 'view_wishlist_header'
			});
		}
	}

	private waitForRegistration(): void {
		if (window.EventAggregationService === undefined) {
			return;
		}

		window.EventAggregationService.subscribeTo(AuthenticationCompletedEvent.EventType, {
			notify: () => {
				this.openWishlistDrawer();
			}
		});
	}

	private openWishlistDrawer(): void {
		// NOTE: this is a workaround because apparently the event gets fired twice
		// when clicking on the Wishlist button when logged in and we need to do the same here after the user has registered/logged in
		// when we remove the multiple buttons on the header we can remove this. https://studyportals.atlassian.net/browse/MIAUW-11457
		document.dispatchEvent(new Event('openWishlistDrawer'));
		document.dispatchEvent(new Event('openWishlistDrawer'));
	}

	private updateName(firstName: string): void {
		const loggedInWrapper = document.getElementById('HeaderLoggedIn');
		const loginName = loggedInWrapper?.getElementsByClassName('LoggedInUserText')[0];

		if (loginName) {
			loginName.innerHTML = firstName;
		}
	}

	private requestMenu(Student: IStudent): void {
		const loginWrapper = document.getElementById('HeaderLogin');
		const loggedInWrapper = document.getElementById('HeaderLoggedIn');
		const loginName = loggedInWrapper?.getElementsByClassName('LoggedInUserText')[0];

		if (!loginName) {
			console.error(`HeaderLogin.requestMenu: #HeaderLogin or #HeaderLoggedInTemplate not in the template`);
			return;
		}

		this.loggedInMenuVisible = true;
		this.updateName(Student.first_name || '');
		loginWrapper?.classList.add('Hidden');
		loggedInWrapper?.classList.remove('Hidden');

		// Fire an event when the header login template has compiled so we can initialize the FavouriteCounter
		document.dispatchEvent(new Event('header_login_updated'));

		this.addEventListenersForWishlistButtons();

		if (typeof Student.first_name !== 'undefined') {
			this.addEventListenerForLoggedInToggle();
		}

		this.addEventListenerForSignOut(loggedInWrapper);
	}

	private addEventListenerForSignOut(loggedInWrapper: HTMLElement): void {
		const $signOutLink = loggedInWrapper?.querySelector('#signOutLink');

		$signOutLink?.addEventListener('click', (event) => {
			event.preventDefault();
			void this.authService?.logout();
		});
	}

	private addEventListenerForLoggedInToggle(): void {
		const loggedInToggle = document.getElementById('LoggedInToggle');
		loggedInToggle?.addEventListener('click', () => {
			document.getElementById('LoggedInDropDown')?.classList.toggle('Selected');
		});
	}

	private showProfileIntroductionIcon(): void {
		const notifictionIcons = document.querySelectorAll('.ProfilePageIntroductionIcon');
		if (notifictionIcons.length === 0) return;

		if (!this.canShowNotificationIcon()) return;

		notifictionIcons.forEach((icon) => {
			icon.classList.remove('Hidden');
		});
	}

	private canShowNotificationIcon(): boolean {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
		const profileIntroductionShown = DataStorage.retrieve('ProfilePageIntroductionShown');
		if (window.location.pathname === '/account/') return false;
		return !profileIntroductionShown;
	}

	public addEventListenerMobileLoginButton(): void {
		const loginButton = document.getElementsByClassName('js-user')[0];

		loginButton.addEventListener('click', () => {
			if (!this.loggedInStatus) {
				const command = new RenderIncentiveCommand(Referrer.HEADER);
				this.authService?.renderIncentive(command);
				this.toggleHeaderFlyOut();
			} else {
				document.querySelector('.AccountModule')?.classList.add('InView');
			}
		});
		return;
	}
}

document.addEventListener('DOMContentLoaded', () => {
	const headerLogin = new HeaderLogin();
	headerLogin.init();
});
